"use client";

import { useSession } from "@/components/SupabaseProvider";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export type UpdatePaymentSource = "wall" | "banner" | "landing";

export type UpdatePaymentOptions = {
  /** Counts "Failed Payment Wall Update Clicked". Defaults to every source
   *  but the landing, whose own redirect is no click; its retry button is. */
  click?: boolean;
};

// "redirected": off to Stripe. "nothing_due": nothing to pay after all.
// "failed": the payment page didn't open (the user was told).
export type UpdatePaymentResult = "redirected" | "nothing_due" | "failed";

// PUT /billing/subscription, flow "update_payment".
type UpdatePaymentResponse = {
  kind?: "hosted_invoice" | "payment_method_update" | "nothing_due";
  url?: string | null;
  portal_url?: string | null;
  reason?: string;
};

export const PAYMENT_UP_TO_DATE_MESSAGE = "Your payment is up to date.";

// Sends the signed-in user to fix a failed renewal: the failed invoice's
// Stripe-hosted page (pay it now with any card, saved for next renewals),
// or the billing portal's add-a-card page when there's no open invoice.
// klap-api picks (PUT /billing/subscription, flow "update_payment"), and
// answers "nothing_due" when there's nothing to pay: paid since, or an old
// past-due plan a newer active plan replaced, whose invoice a new card would
// pay a second time.
export const useUpdatePayment = () => {
  const session = useSession();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const redirected = useRef(false);

  // Back from Stripe's page through the back-forward cache, the page comes
  // back as it left: still pending, with a list from before the payment.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !redirected.current) return;
      redirected.current = false;
      setIsPending(false);
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [queryClient]);

  const updatePayment = useCallback(
    async (
      source: UpdatePaymentSource,
      properties: Record<string, unknown> = {},
      { click = source !== "landing" }: UpdatePaymentOptions = {},
    ): Promise<UpdatePaymentResult> => {
      if (!session?.access_token) return "failed";
      if (click)
        posthog?.capture("Failed Payment Wall Update Clicked", {
          source,
          ...properties,
        });
      setIsPending(true);
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/billing/subscription`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ flow: "update_payment" }),
          },
        );
        const body: UpdatePaymentResponse = await res.json().catch(() => ({}));
        if (res.ok && body?.kind === "nothing_due") {
          // No Stripe page: the fresh list drops the banner, and the caller
          // closes the wall. The landing says so in its own words.
          await queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
          setIsPending(false);
          if (source !== "landing") toast.success(PAYMENT_UP_TO_DATE_MESSAGE);
          return "nothing_due";
        }
        const url = body?.url ?? body?.portal_url;
        if (!res.ok || !url) throw new Error(`status ${res.status}`);
        redirected.current = true;
        window.location.assign(url);
        return "redirected";
      } catch {
        setIsPending(false);
        toast.error(
          "Couldn't open the payment page. Try again, or update your card from Billing.",
        );
        return "failed";
      }
    },
    [session?.access_token, posthog, queryClient],
  );

  return { updatePayment, isPending };
};
